import { api } from './api.service';

/**
 * Attendance module API — Cloud FE → Cloud BE → tunnel → local command-center.
 * The response is DOUBLE-wrapped: the command-center returns its own
 * { success, data, pagination } envelope, which the cloud BE then wraps again as
 * { success, message, data: <cc-envelope> }. So axios `resp.data.data` is the
 * command-center envelope — we peel one more level to reach the actual payload.
 */
const base = (nodeId: string) => `/nodes/${nodeId}/analytics/attendance`;
/** Peel the cloud-BE envelope (resp.data.data) and then the inner CC envelope, if present. */
const unwrap = (resp: any) => {
  const inner = resp?.data?.data;
  return inner && typeof inner === 'object' && 'success' in inner && 'data' in inner ? inner.data : inner;
};
/** Like unwrap but keeps the CC envelope ({ data, pagination }) for paginated reads. */
const unwrapPaginated = (resp: any) => resp?.data?.data;

// ── Config (module toggle / mode) ──
export async function getAttendanceConfig(nodeId: string) {
  return unwrap(await api.get(`${base(nodeId)}/config`));
}
export async function updateAttendanceConfig(nodeId: string, patch: any) {
  return unwrap(await api.put(`${base(nodeId)}/config`, patch));
}

// ── Presence board ──
export async function getAttendanceToday(nodeId: string, date?: string) {
  return unwrap(await api.get(`${base(nodeId)}/today`, { params: date ? { date } : {} }));
}

// ── Timeline: unified employee + registered-face in/out logs for one work day ──
export async function getAttendanceTimeline(
  nodeId: string,
  params: { date?: string; camera_id?: string; search?: string } = {}
) {
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) if (v) clean[k] = v;
  return unwrap(await api.get(`${base(nodeId)}/timeline`, { params: clean }));
}

// ── Employees ──
export async function listEmployees(nodeId: string, params: any = {}) {
  return unwrap(await api.get(`${base(nodeId)}/employees`, { params }));
}
export async function getEmployee(nodeId: string, id: string) {
  return unwrap(await api.get(`${base(nodeId)}/employees/${id}`));
}
export async function createEmployee(nodeId: string, data: any) {
  return unwrap(await api.post(`${base(nodeId)}/employees`, data));
}
export async function updateEmployee(nodeId: string, id: string, data: any) {
  return unwrap(await api.put(`${base(nodeId)}/employees/${id}`, data));
}
export async function deleteEmployee(nodeId: string, id: string) {
  return unwrap(await api.delete(`${base(nodeId)}/employees/${id}`));
}
export async function createEmployeeFromEvent(nodeId: string, data: any) {
  return unwrap(await api.post(`${base(nodeId)}/employees/from-event`, data));
}
export async function linkEventToEmployee(nodeId: string, id: string, event_id: string) {
  return unwrap(await api.post(`${base(nodeId)}/employees/${id}/face-photos/from-event`, { event_id }));
}

// ── Enrollment photo upload — convert File[] to base64 JSON (multipart can't cross the tunnel) ──
// Photos are downscaled client-side first: 5 raw phone photos can be tens of MB, which blows
// past the body-size limits along FE → BE → tunnel → CC (413 Payload Too Large). Face embeddings
// don't need more than ~1280px, so we cap the longest edge and re-encode as JPEG.
const MAX_PHOTO_DIM = 1280;
const PHOTO_JPEG_QUALITY = 0.85;
async function readFileBase64(file: File): Promise<{ base64: string; content_type: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve({ base64: result.split(',')[1] || '', content_type: file.type || 'image/jpeg' });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
async function fileToBase64(file: File): Promise<{ base64: string; content_type: string }> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_PHOTO_DIM / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const dataUrl = canvas.toDataURL('image/jpeg', PHOTO_JPEG_QUALITY);
    return { base64: dataUrl.split(',')[1] || '', content_type: 'image/jpeg' };
  } catch {
    // Fallback (e.g. unsupported format): send the original file untouched.
    return readFileBase64(file);
  }
}
/** Remove one enrolled photo / unlink a face from an employee. */
export async function removeEmployeePhoto(nodeId: string, employeeId: string, photoId: string) {
  return unwrap(await api.delete(`${base(nodeId)}/employees/${employeeId}/face-photos/${photoId}`));
}

export async function uploadEmployeePhotos(nodeId: string, id: string, files: File[]) {
  const images = await Promise.all(files.map(fileToBase64));
  return unwrap(await api.post(`${base(nodeId)}/employees/${id}/face-photos`, { images }));
}
/** Fetch an enrollment photo through the proxy and return a data: URL (proxy wraps binary as base64). */
export async function getEmployeePhotoDataUrl(nodeId: string, photoId: string): Promise<string | null> {
  try {
    const resp = await api.get(`${base(nodeId)}/face-photos/${photoId}/images/photo`);
    const d = resp?.data?.data;
    if (d?._binary && d?.base64) return `data:${d.contentType || 'image/jpeg'};base64,${d.base64}`;
    return null;
  } catch {
    return null;
  }
}

// ── Per-employee reads ──
export async function getEmployeeDay(nodeId: string, id: string, date?: string) {
  return unwrap(await api.get(`${base(nodeId)}/employees/${id}/day`, { params: date ? { date } : {} }));
}
export async function getEmployeeSightings(nodeId: string, id: string, params: any = {}) {
  const resp = await api.get(`${base(nodeId)}/employees/${id}/sightings`, { params });
  return unwrapPaginated(resp); // CC envelope { data, pagination }
}
export async function getEmployeeMonthly(nodeId: string, id: string, month?: string) {
  return unwrap(await api.get(`${base(nodeId)}/employees/${id}/monthly`, { params: month ? { month } : {} }));
}

// ── Payroll: org-wide monthly present/absent summary + its hand-entered inputs ──
export async function getMonthlyReport(nodeId: string, month?: string) {
  return unwrap(await api.get(`${base(nodeId)}/monthly-report`, { params: month ? { month } : {} }));
}
export async function setPayrollAdjustments(nodeId: string, month: string, employeeId: string, patch: any) {
  return unwrap(await api.put(`${base(nodeId)}/payroll/${month}/adjustments/${employeeId}`, patch));
}
export async function setPayrollBulkAdjustments(nodeId: string, month: string, patch: any) {
  return unwrap(await api.put(`${base(nodeId)}/payroll/${month}/bulk-adjustments`, patch));
}
export async function addPayrollHoliday(nodeId: string, date: string, name: string) {
  return unwrap(await api.post(`${base(nodeId)}/payroll/holidays`, { holiday_date: date, name }));
}
export async function removePayrollHoliday(nodeId: string, date: string) {
  return unwrap(await api.delete(`${base(nodeId)}/payroll/holidays/${date}`));
}
export async function setPayrollWorkingDays(nodeId: string, month: string, workingDays: number | null) {
  return unwrap(await api.put(`${base(nodeId)}/payroll/${month}/working-days`, { working_days: workingDays }));
}
export async function setPayrollSettings(nodeId: string, settings: Record<string, boolean | number>) {
  return unwrap(await api.put(`${base(nodeId)}/payroll/settings`, settings));
}

// ── Unknown face events (for register/link) ──
// Model key is `face` — the command-center's model map (modelEventsController) maps that to
// the facial_recognition_events table; the raw table name is NOT a valid model key (→ 400).
export async function listUnknownFaceEvents(nodeId: string, params: any = {}) {
  // Dedicated unknown-only route: reachable with the narrow `ai_events.unknown_faces`
  // permission, so an attendance role can run the link flow without ai_events.view (which
  // also carries the identified-person feeds and the AI report pages). `is_known` is
  // pinned server-side; sending it here would be ignored.
  //
  // `from`/`to` (epoch seconds) pass through the proxy's query-string relay into the
  // command-center's SQL filter, so a windowed request is narrowed at the database rather
  // than after crossing the tunnel.
  const resp = await api.get(`/nodes/${nodeId}/analytics/events/face/unknown`, {
    params: { limit: 40, ...params },
  });
  // Double-wrapped: resp.data.data is the CC envelope { success, data: rows, pagination }.
  const inner = resp?.data?.data;
  return inner && typeof inner === 'object' && 'data' in inner ? inner.data : inner;
}
/** Fetch an event image through the proxy and return a data: URL (proxy wraps binary as base64). */
export async function getEventImageDataUrl(nodeId: string, eventId: string, type = 'faceCrop'): Promise<string | null> {
  try {
    const resp = await api.get(`/nodes/${nodeId}/analytics/events/face/${eventId}/images/${type}`);
    const d = resp?.data?.data;
    if (d?._binary && d?.base64) return `data:${d.contentType || 'image/jpeg'};base64,${d.base64}`;
    return null;
  } catch {
    return null;
  }
}

// ── Camera entry/exit config ──
export async function listAttendanceCameras(nodeId: string) {
  return unwrap(await api.get(`${base(nodeId)}/cameras`));
}
export async function upsertAttendanceCamera(nodeId: string, cameraId: string, data: any) {
  return unwrap(await api.put(`${base(nodeId)}/cameras/${cameraId}`, data));
}
export async function deleteAttendanceCamera(nodeId: string, cameraId: string) {
  return unwrap(await api.delete(`${base(nodeId)}/cameras/${cameraId}`));
}
